import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { FolderColorDialog } from "./FolderColorDialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent } from "./ui/dropdown-menu";
import { Plus, SignOut, Tag as TagIcon, FolderSimple, FolderPlus, Stack, Recycle, DeviceMobile, LinkSimple, DotsThreeVertical, PencilSimple, Trash, CaretDown, CaretRight, CaretLeft, Star, CalendarBlank, FileText, Image as ImageIcon, FolderMinus, CheckSquare, PaintBucket } from "@phosphor-icons/react";
import { ALL_CAPTURES_FILTER, TODAY_FILTER } from "../lib/captureFilters";

const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 480;
const DEFAULT_SIDEBAR_WIDTH = 256;

// Compact MM/DD date for the inline note list under a selected tag - the
// sidebar is narrow, so the full year (shown on dashboard cards) is dropped.
function formatShortDate(isoString) {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit" });
}

export function Sidebar({
  tags, folders, selectedTagId, onSelectTag, onNewTag, onDeleteTag, onRenameTag, onGenerateTagLink,
  onOpenTrash, trashActive, onNewFolder, onDeleteFolder, onRenameFolder, onMoveTagToFolder,
  onUpdateFolderColor, onBulkMoveTagsToFolder,
  captures, onOpenNote, collapsed, onToggleCollapse,
}) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const [editingTagId, setEditingTagId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [folderEditValue, setFolderEditValue] = useState("");
  const [expandedFolderIds, setExpandedFolderIds] = useState([]);
  const [dragOverTarget, setDragOverTarget] = useState(null); // folder id | "ungrouped" | null
  const [colorEditFolder, setColorEditFolder] = useState(null);
  // "Batch manage tags" mode - toggled on from the button at the bottom of
  // the tag list. While active, every non-favorites tag row shows a
  // checkbox instead of its normal click-to-filter/drag-to-reorder
  // behavior, and the new-tag/new-folder buttons are swapped for a bulk
  // "add to folder" action bar.
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  // Freely-draggable sidebar width (the sidebar is always the leftmost
  // element on the page, so its left edge sits at viewport x=0 - the mouse's
  // clientX during drag directly is the new width).
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const widthDraggingRef = useRef(false);

  const handleWidthDragMove = useCallback((e) => {
    if (!widthDraggingRef.current) return;
    setSidebarWidth(Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, e.clientX)));
  }, []);

  const stopWidthDrag = useCallback(() => {
    widthDraggingRef.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("mousemove", handleWidthDragMove);
    document.removeEventListener("mouseup", stopWidthDrag);
  }, [handleWidthDragMove]);

  const startWidthDrag = (e) => {
    e.preventDefault();
    widthDraggingRef.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleWidthDragMove);
    document.addEventListener("mouseup", stopWidthDrag);
  };

  useEffect(() => stopWidthDrag, [stopWidthDrag]);


  const startRename = (tag) => {
    setEditingTagId(tag.id);
    setEditValue(tag.name);
  };

  const commitRename = (tagId) => {
    const trimmed = editValue.trim();
    if (trimmed) onRenameTag(tagId, trimmed);
    setEditingTagId(null);
  };

  const startRenameFolder = (folder) => {
    setEditingFolderId(folder.id);
    setFolderEditValue(folder.name);
  };

  const commitRenameFolder = (folderId) => {
    const trimmed = folderEditValue.trim();
    if (trimmed) onRenameFolder(folderId, trimmed);
    setEditingFolderId(null);
  };

  const toggleFolderExpanded = (folderId) => {
    setExpandedFolderIds((prev) => (prev.includes(folderId) ? prev.filter((id) => id !== folderId) : [...prev, folderId]));
  };

  const handleTagDrop = (e, folderId) => {
    e.preventDefault();
    setDragOverTarget(null);
    const tagId = e.dataTransfer.getData("text/tag-id");
    if (tagId) onMoveTagToFolder(tagId, folderId);
  };

  const toggleMultiSelectMode = () => {
    setMultiSelectMode((prev) => !prev);
    setSelectedTagIds([]);
  };

  const toggleTagSelection = (tagId) => {
    setSelectedTagIds((prev) => (prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]));
  };

  const handleBulkMove = (folderId) => {
    if (selectedTagIds.length === 0) return;
    onBulkMoveTagsToFolder(selectedTagIds, folderId);
    setMultiSelectMode(false);
    setSelectedTagIds([]);
  };

  const renderTagRow = (tag) => (
    <div
      key={tag.id}
      draggable={!tag.is_favorites && !multiSelectMode}
      onDragStart={(e) => e.dataTransfer.setData("text/tag-id", tag.id)}
      className="group flex items-center"
    >
      {multiSelectMode && !tag.is_favorites && (
        <Checkbox
          data-testid={`tag-select-checkbox-${tag.id}`}
          checked={selectedTagIds.includes(tag.id)}
          onCheckedChange={() => toggleTagSelection(tag.id)}
          className="ml-3 rounded-none shrink-0"
        />
      )}
      {editingTagId === tag.id ? (
        <Input
          data-testid={`tag-rename-input-${tag.id}`}
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => commitRename(tag.id)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commitRename(tag.id);
            if (e.key === "Escape") setEditingTagId(null);
          }}
          className="flex-1 h-8 rounded-none text-sm mx-1"
        />
      ) : (
        <button
          data-testid={`tag-item-${tag.id}`}
          onClick={() => (multiSelectMode ? !tag.is_favorites && toggleTagSelection(tag.id) : onSelectTag(tag.id))}
          className={`flex-1 text-left px-3 py-2 text-sm font-medium flex items-center justify-between gap-2 transition-colors ${tag.is_favorites || multiSelectMode ? "" : "cursor-grab active:cursor-grabbing"} ${selectedTagId === tag.id && !trashActive && !multiSelectMode ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
        >
          <span className="truncate flex items-center gap-1.5">
            {tag.is_favorites ? <Star size={13} weight="fill" className={selectedTagId === tag.id && !trashActive ? "" : "text-amber-500"} /> : <TagIcon size={13} />}
            {tag.name}
          </span>
          <span className="font-mono-tag text-[10px] opacity-70">{tag.capture_count}</span>
        </button>
      )}
      {!multiSelectMode && (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={`tag-menu-${tag.id}`}
            onClick={(e) => e.stopPropagation()}
            className="opacity-0 group-hover:opacity-100 px-1.5 text-muted-foreground hover:text-foreground transition-opacity"
          >
            <DotsThreeVertical size={15} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-none">
          <DropdownMenuItem data-testid={`tag-generate-link-${tag.id}`} onClick={() => onGenerateTagLink(tag.id)} className="gap-2">
            <LinkSimple size={14} /> {t("sidebar.generateLinkTooltip")}
          </DropdownMenuItem>
          {!tag.is_favorites && (
            <>
              <DropdownMenuItem data-testid={`tag-rename-${tag.id}`} onClick={() => startRename(tag)} className="gap-2">
                <PencilSimple size={14} /> {t("sidebar.renameTag")}
              </DropdownMenuItem>
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid={`tag-move-to-folder-${tag.id}`} className="gap-2">
                  <FolderSimple size={14} /> {t("sidebar.addToFolder")}
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="rounded-none">
                  {folders.length === 0 ? (
                    <p className="px-2 py-1.5 text-xs text-muted-foreground italic">{t("sidebar.noFoldersYet")}</p>
                  ) : (
                    folders.map((folder) => (
                      <DropdownMenuItem
                        key={folder.id}
                        data-testid={`tag-move-to-folder-option-${tag.id}-${folder.id}`}
                        disabled={tag.folder_id === folder.id}
                        onClick={() => onMoveTagToFolder(tag.id, folder.id)}
                        className="gap-2"
                      >
                        <FolderSimple size={14} /> {folder.name}
                      </DropdownMenuItem>
                    ))
                  )}
                  {tag.folder_id && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem data-testid={`tag-remove-from-folder-${tag.id}`} onClick={() => onMoveTagToFolder(tag.id, null)} className="gap-2">
                        <FolderMinus size={14} /> {t("sidebar.removeFromFolder")}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid={`tag-delete-${tag.id}`} onClick={() => onDeleteTag(tag.id)} className="gap-2 text-destructive focus:text-destructive">
                <Trash size={14} /> {t("sidebar.deleteTag")}
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      )}
    </div>
  );

  // Accordion-style quick-open list: only ever shown under whichever filter
  // is currently selected (selectedTagId is single-valued, so at most one
  // list is expanded at a time) - reused for real tags as well as the
  // "今日"/"无标签笔记" virtual filters (matchValue mirrors whatever value
  // that button passes to onSelectTag). Reuses the `captures` already
  // fetched for the active filter - no extra request - so this stays in
  // sync with the main grid for free. Clicking a note opens it straight
  // into the existing 2-pane split-view tab logic.
  const renderNoteList = (matchValue, testKey) => {
    if (selectedTagId !== matchValue || trashActive || !captures) return null;
    return (
      <div className="pl-4 border-l border-border ml-4 mb-1" data-testid={`tag-note-list-${testKey}`}>
        {captures.length === 0 ? (
          <p className="text-xs text-muted-foreground px-3 py-1.5 italic">{t("sidebar.tagNoteListEmptyHint")}</p>
        ) : (
          captures.map((capture) => (
            <button
              key={capture.id}
              data-testid={`tag-note-item-${capture.id}`}
              onClick={() => onOpenNote?.(capture)}
              className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-secondary transition-colors"
            >
              {capture.type === "text" ? <FileText size={12} className="shrink-0 text-muted-foreground" /> : <ImageIcon size={12} className="shrink-0 text-muted-foreground" />}
              <span className="truncate flex-1">{capture.title || t("dashboard.untitledFallback")}</span>
              <span className="font-mono-tag text-[10px] text-muted-foreground shrink-0">{formatShortDate(capture.updated_at || capture.created_at)}</span>
            </button>
          ))
        )}
      </div>
    );
  };
  const renderTagNoteList = (tag) => renderNoteList(tag.id, tag.id);

  const favoritesTag = tags.find((tag) => tag.is_favorites);
  const ungroupedTags = tags.filter((tag) => !tag.folder_id && !tag.is_favorites).sort((a, b) => a.name.localeCompare(b.name));

  if (collapsed) {
    return (
      <aside className="w-12 shrink-0 border-r border-border bg-card flex flex-col items-center py-6" data-testid="sidebar-collapsed">
        <button
          data-testid="sidebar-expand-button"
          onClick={onToggleCollapse}
          title={t("sidebar.expandSidebar")}
          className="p-2 text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
        >
          <CaretRight size={16} weight="bold" />
        </button>
      </aside>
    );
  }

  return (
    <aside
      className="w-full relative shrink-0 border-r border-border bg-card flex flex-col h-full"
      style={{ width: sidebarWidth }}
      data-testid="sidebar"
    >
      <div className="p-6 border-b border-border space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-mono-tag text-[10px] text-primary">NOTE</p>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button
              data-testid="sidebar-collapse-button"
              onClick={onToggleCollapse}
              title={t("sidebar.collapseSidebar")}
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              <CaretLeft size={14} weight="bold" />
            </button>
          </div>
        </div>
        <p className="font-display text-lg font-bold truncate">{user?.name}</p>
      </div>

      <nav className="flex-1 overflow-y-auto px-4 pt-4 space-y-1">
        <button
          data-testid="tag-filter-show-all"
          onClick={() => onSelectTag(ALL_CAPTURES_FILTER)}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${selectedTagId === ALL_CAPTURES_FILTER && !trashActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
        >
          <Stack size={16} /> {t("sidebar.showAll")}
        </button>
        {favoritesTag && renderTagRow(favoritesTag)}
        {favoritesTag && renderTagNoteList(favoritesTag)}
        <button
          data-testid="tag-filter-today"
          onClick={() => onSelectTag(TODAY_FILTER)}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${selectedTagId === TODAY_FILTER && !trashActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
        >
          <CalendarBlank size={16} /> {t("sidebar.today")}
        </button>
        {renderNoteList(TODAY_FILTER, "today")}
        <button
          data-testid="tag-filter-all"
          onClick={() => onSelectTag(null)}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${!selectedTagId && !trashActive ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}
        >
          <FolderSimple size={16} /> {t("sidebar.untagged")}
        </button>
        {renderNoteList(null, "untagged")}

        {folders.map((folder) => {
          const folderTags = tags.filter((tag) => tag.folder_id === folder.id).sort((a, b) => a.name.localeCompare(b.name));
          const isExpanded = expandedFolderIds.includes(folder.id);
          const isDragOver = dragOverTarget === folder.id;
          return (
            <div key={folder.id}>
              <div
                className={`group flex items-center transition-colors ${isDragOver ? "bg-accent ring-1 ring-primary" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOverTarget(folder.id);
                }}
                onDragLeave={() => setDragOverTarget((prev) => (prev === folder.id ? null : prev))}
                onDrop={(e) => handleTagDrop(e, folder.id)}
              >
                {editingFolderId === folder.id ? (
                  <Input
                    data-testid={`folder-rename-input-${folder.id}`}
                    autoFocus
                    value={folderEditValue}
                    onChange={(e) => setFolderEditValue(e.target.value)}
                    onBlur={() => commitRenameFolder(folder.id)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commitRenameFolder(folder.id);
                      if (e.key === "Escape") setEditingFolderId(null);
                    }}
                    className="flex-1 h-8 rounded-none text-sm mx-1"
                  />
                ) : (
                  <button
                    data-testid={`folder-toggle-${folder.id}`}
                    onClick={() => toggleFolderExpanded(folder.id)}
                    className="flex-1 text-left px-3 py-2 text-sm font-medium flex items-center gap-2 hover:bg-secondary transition-colors"
                  >
                    {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                    <FolderSimple size={16} style={folder.color ? { color: folder.color } : undefined} />
                    <span className="truncate flex-1">{folder.name}</span>
                    <span className="font-mono-tag text-[10px] opacity-70">{folderTags.length}</span>
                  </button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`folder-menu-${folder.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="opacity-0 group-hover:opacity-100 px-1.5 text-muted-foreground hover:text-foreground transition-opacity"
                    >
                      <DotsThreeVertical size={15} weight="bold" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-none">
                    <DropdownMenuItem data-testid={`folder-rename-${folder.id}`} onClick={() => startRenameFolder(folder)} className="gap-2">
                      <PencilSimple size={14} /> {t("sidebar.renameFolder")}
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`folder-customize-${folder.id}`} onClick={() => setColorEditFolder(folder)} className="gap-2">
                      <PaintBucket size={14} /> {t("sidebar.customAppearance")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid={`folder-delete-${folder.id}`} onClick={() => onDeleteFolder(folder.id)} className="gap-2 text-destructive focus:text-destructive">
                      <Trash size={14} /> {t("sidebar.deleteFolder")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              {isExpanded && (
                <div className="pl-4 border-l border-border ml-4">
                  {folderTags.length === 0 ? (
                    <p className="text-xs text-muted-foreground px-3 py-1.5 italic">{t("sidebar.folderEmptyHint")}</p>
                  ) : (
                    folderTags.map((tag) => (
                      <div key={tag.id}>
                        {renderTagRow(tag)}
                        {renderTagNoteList(tag)}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}

        <div
          data-testid="ungrouped-tags-dropzone"
          className={`transition-colors ${dragOverTarget === "ungrouped" ? "bg-accent ring-1 ring-primary" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOverTarget("ungrouped");
          }}
          onDragLeave={() => setDragOverTarget((prev) => (prev === "ungrouped" ? null : prev))}
          onDrop={(e) => handleTagDrop(e, null)}
        >
          {ungroupedTags.map((tag) => (
            <div key={tag.id}>
              {renderTagRow(tag)}
              {renderTagNoteList(tag)}
            </div>
          ))}
        </div>

        {!multiSelectMode && (
          <>
            <button
              data-testid="new-tag-button"
              onClick={onNewTag}
              className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <Plus size={16} /> {t("sidebar.newTag")}
            </button>
            <button
              data-testid="new-folder-button"
              onClick={onNewFolder}
              className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <FolderPlus size={16} /> {t("sidebar.newFolder")}
            </button>
          </>
        )}
      </nav>

      <div className="px-4 pb-2">
        {multiSelectMode && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 mb-1 bg-secondary border border-border" data-testid="bulk-select-bar">
            <span className="text-xs font-semibold text-muted-foreground">{t("sidebar.selectedTagsCount", { count: selectedTagIds.length })}</span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="bulk-add-to-folder-button"
                  disabled={selectedTagIds.length === 0}
                  className="text-xs font-semibold text-primary disabled:text-muted-foreground disabled:opacity-50 flex items-center gap-1 px-2 py-1 hover:bg-background transition-colors disabled:hover:bg-transparent"
                >
                  <FolderSimple size={13} /> {t("sidebar.addToFolder")}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-none">
                {folders.length === 0 ? (
                  <p className="px-2 py-1.5 text-xs text-muted-foreground italic">{t("sidebar.noFoldersYet")}</p>
                ) : (
                  folders.map((folder) => (
                    <DropdownMenuItem
                      key={folder.id}
                      data-testid={`bulk-add-to-folder-option-${folder.id}`}
                      onClick={() => handleBulkMove(folder.id)}
                      className="gap-2"
                    >
                      <FolderSimple size={14} style={folder.color ? { color: folder.color } : undefined} /> {folder.name}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        <button
          data-testid="bulk-select-toggle-button"
          onClick={toggleMultiSelectMode}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${multiSelectMode ? "text-destructive hover:bg-destructive/10" : "text-muted-foreground hover:text-foreground hover:bg-secondary"}`}
        >
          <CheckSquare size={16} /> {multiSelectMode ? t("sidebar.exitBulkSelect") : t("sidebar.bulkSelectMode")}
        </button>
      </div>

      <div className="px-4 pb-4 space-y-1">
        <button
          data-testid="trash-nav-button"
          onClick={onOpenTrash}
          className={`w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 border-t border-border pt-4 transition-colors ${trashActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
        >
          <Recycle size={16} /> {t("sidebar.trash")}
        </button>
        <Link
          to="/install"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="install-guide-nav-link"
          className="w-full text-left px-3 py-2 text-sm font-medium flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <DeviceMobile size={16} /> {t("sidebar.installToPhone")}
        </Link>
      </div>

      <div className="p-4 border-t border-border">
        <Button
          data-testid="logout-button"
          variant="outline"
          onClick={logout}
          className="w-full rounded-none gap-2 justify-start"
        >
          <SignOut size={16} /> {t("sidebar.logout")}
        </Button>
      </div>
      <div
        data-testid="sidebar-resize-handle"
        onMouseDown={startWidthDrag}
        title={t("sidebar.resizeSidebar")}
        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-primary/50 active:bg-primary transition-colors"
      />
      <FolderColorDialog
        folder={colorEditFolder}
        onOpenChange={(open) => !open && setColorEditFolder(null)}
        onSave={onUpdateFolderColor}
      />
    </aside>
  );
}
